import type { Hive } from '../../src/modules/hive'
import { setCurrentlySyncing, setLastSyncTime } from '../../src/redux/actions/common'
import { syncAll } from '../../src/redux/actions/entities'
import { Auth_forceLogout } from '../Auth/Auth.forceLogout'
import { Cale_sync } from '../Cale/Cale.sync'
import type { Base_Bus } from '../Base/Base.Bus'
import { Cust_hasSingleLocation } from '../Cust/Cust.serverPrefs'
import { Cust_sync } from '../Cust/Cust.sync'
import { DS_getData, DS_setData } from '../DS/DS.Data'
import { E2E_dispatchSyncSuccess } from '../E2E/E2E.dispatch'
import { Form_sync } from '../Form/Form.sync'
import { I18N_sync } from '../I18N/I18N.sync'
import { IDB_actionTypesWrite } from '../IDB/IDB.table.actionType'
import { IDB_fixesWrite } from '../IDB/IDB.table.fix'
import { IDB_issuesWrite } from '../IDB/IDB.table.issue'
import { IDB_locationsWrite } from '../IDB/IDB.table.location'
import { IDB_locationFocusListsWrite } from '../IDB/IDB.table.locationFocusList'
import { IDB_proceduresWrite } from '../IDB/IDB.table.procedure'
import { IDB_reportsReadAll } from '../IDB/IDB.table.report'
import { IDB_smartDataOrderingsWrite } from '../IDB/IDB.table.smartDataOrdering'
import { IDB_unitsWrite } from '../IDB/IDB.table.unit'
import { IDB_unitListsWrite } from '../IDB/IDB.table.unitList'
import { IDB_unitTypesWrite } from '../IDB/IDB.table.unitType'
import { Imag_cacheImages } from '../Imag/Imag.cache'
import { Imag_getImageUrlsForUnits } from '../Imag/Imag.getUrls'
import { Gami_sync } from '../Gami/Gami.sync'
import { Loca_syncTimelineEvents } from '../Loca/Loca.syncTimelineEvents'
import { Loca_updateCurrentTimeline } from '../Loca/Loca.updateCurrentTimeline'
import { Logg_event } from '../Logg/Logg.event'
import { Logg_sync } from '../Logg/Logg.Log'
import { Logg_timer } from '../Logg/Logg.timer'
import { Plat_getDeviceInfo } from '../Plat/Plat.device'
import { Post_sync } from '../Post/Post.sync'
import { Rpts_sync } from '../Rpts/Rpts.sync.sync'
import { Rpts_upload } from '../Rpts/Rpts.sync.upload'
import { SD_sync } from '../SD/SD.sync'
import { Serv_send } from '../Serv/Serv.socketClient'
import type { Serv_ServerResponseMapping } from '../Serv/Serv.types'
import { Sync_clearOldData } from './Sync.clearOldData'
import { Sync_syncConfirmedActions } from './Sync.confirmedActions'
import { Sync_reset } from './Sync.initialise'
import { Sync_syncProcessFlow } from './Sync.processFlow'
import { Sync_syncCommon } from './Sync.syncCommon'
import { Todo_requestSync } from '../Todo/Todo.sync'
import { Util_unixTime } from '../Util/Util.date'
import ActivityStore from '../../src/stores/activity'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import { Auth_syncUserData } from '../Auth/Auth.loginSync'
import { Flow_syncFeedback } from '../Flow/Flow.Feedback'

export type Sync_SyncResult = {
    response: Serv_ServerResponseMapping['sync.v2']['result'] &
        Serv_ServerResponseMapping['forms.getAll'] &
        Serv_ServerResponseMapping['buzz.getFixes']
}

export const Sync_status = {
    ok: 'ok',
    missing: 'missing',
}

const tenSeconds = 10 * 1000

export async function Sync_syncMaster(logger: ClientLogger, hive: Hive, bus: Base_Bus) {
    hive.dispatch(setCurrentlySyncing(true))

    try {
        logger.log(`Sync_syncMaster`)

        let since = hive.getlastDBupdate('sync').value
        const syncTimer = Logg_timer()
        Logg_event('Start', { label: 'Sync' })

        if (DS_getData().shouldResync) {
            since = 0
            DS_setData('RsyncTriggered', () => ({ shouldResync: false }))
        }

        // Sync down feature flags first so they can be relied upon when deciding what endpoint api to use
        await Cust_sync()

        // Request syncs that are handled on bus
        requestSyncsOnBus(bus)

        logger.log(`requesting sync data from server`, { since })
        // Make requests for sync data
        const syncP = Serv_send('sync.v2', { since, shouldFilterUnitListItemsByDate: false })
        const formsP = Serv_send('forms.getAll', { since, shouldFilterQuestionsOnDate: false })
        const fixesP = Serv_send('buzz.getFixes', { since })

        // Auth sync
        const customer = DS_getData().customer
        await Auth_syncUserData(customer)

        // Update sync interval
        syncP.then(({ syncInterval }) => {
            if (syncInterval == null || DS_getData().syncInterval === syncInterval) return
            if (syncInterval < tenSeconds) {
                logger.error('Badly configured syncInterval', {})
                return
            }
            DS_setData('SyncIntervalChanged', () => ({ syncInterval }))
            Sync_reset(bus)
        })

        // Disable app metrics
        syncP.then(({ disableAppCenterMetrics }) => {
            if (disableAppCenterMetrics !== DS_getData().appCenterMetricsDisabled) {
                DS_setData('DisableAppCenterMetrics', () => ({
                    appCenterMetricsDisabled: disableAppCenterMetrics,
                }))
            }
        })

        // Send un-uploaded report count to Sentry for monitoring
        let notUploadedCount
        try {
            const reports = await IDB_reportsReadAll()
            const notUploadedReports = reports.filter((report) => !report.uploaded)
            notUploadedCount = notUploadedReports.length

            if (notUploadedCount > 0) {
                logger.log(`not uploaded reports`, { count: notUploadedCount })
            }

            // Send device info to the server
            const device = Plat_getDeviceInfo()
            if (device != null) {
                const information = {
                    deviceId: device.uuid,
                    os: device.platform,
                    pendingReports: notUploadedCount,
                    currentVersion: ENV.releaseVersion,
                }

                await Serv_send('sync.info', information)
                logger.log(`sent device info`)
            }

            // If there are more than 8 non-uploaded reports. Report it to sentry.
            if (notUploadedCount > 8) {
                logger.error(`user has lots of unuploaded reports`, { count: notUploadedCount })
            }
        } catch (error) {
            logger.error(error, {}, { context: 'Error when reporting report count' })
        }

        // userMustLogOut - force the user to log out if they have no reports to upload
        if ((await syncP).userMustLogOut && notUploadedCount === 0 && !ActivityStore.hasCurrentActivity()) {
            logger.warn(`user must log out`)
            Auth_forceLogout()
            return { status: [] }
        }

        const response = {
            ...(await syncP).result,
            ...(await formsP),
            ...(await fixesP),
        }

        logger.log(`received data from server`)
        const result = await Sync_managePromises(logger, hive, { response }, { requireAll: since === 0, since })
        logger.log(`saved data from server`)

        // Set the last sync time after successfully syncing
        const time = new Date((await syncP).time).getTime()
        hive.setlastDBupdate('sync', Util_unixTime(time))

        hive.dispatch(setLastSyncTime(Date.now()))

        DS_setData('SyncSuccess', () => ({
            forcedLogoutCanLogoutWithoutWarning: false,
            lastSyncTime: time,
        }))

        Logg_event('SyncEnd', { duration: syncTimer.getDurationInWholeSeconds() })
        E2E_dispatchSyncSuccess()

        return result
    } catch (error) {
        logger.error(error, { tags: { context: 'Error during sync' } })
        throw error
    } finally {
        hive.dispatch(setCurrentlySyncing(false))
        logger.log(`done`)
    }
}

export async function Sync_managePromises(
    logger: ClientLogger,
    hive: Hive,
    syncResult: Sync_SyncResult,
    options: { requireAll: boolean; since: number }
) {
    // locations
    const locationsP = IDB_locationsWrite(
        (syncResult.response.locations || []).map((location) => ({
            ...location,
            locationId: location.id,
        }))
    )

    // locationFocusLists
    const locationFocusListsP = IDB_locationFocusListsWrite(
        (syncResult.response.locationFocusLists || []).map((focusList) => ({
            ...focusList,
            locationFocusListId: focusList.id,
        }))
    )

    // Simple writes to disk
    const issuesP = IDB_issuesWrite(syncResult.response.issues || [])
    const smartDataOrderingsP = IDB_smartDataOrderingsWrite(syncResult.response.smartDataOrdering || [])
    const actionTypesP = IDB_actionTypesWrite(syncResult.response.actions || [])
    const unitsP = IDB_unitsWrite(syncResult.response.units || [])
    const unitTypesP = IDB_unitTypesWrite(syncResult.response.unitTypes || [])
    const unitListsP = IDB_unitListsWrite(syncResult.response.unitLists || [])
    const campaignsP = IDB_proceduresWrite(syncResult.response.campaigns || [])
    const fixesWriteP = IDB_fixesWrite(syncResult.response.fixes || [])

    // Custom sync logic
    const reportsP = Rpts_sync(syncResult)
    const smartDataP = SD_sync(syncResult)
    const feedbacksP = Flow_syncFeedback()
    const timelineEventsP =
        !Cust_hasSingleLocation() &&
        locationsP.then(() => Loca_syncTimelineEvents(hive, syncResult, Loca_updateCurrentTimeline))
    const formsWriteP = locationsP.then(() => Form_sync(syncResult.response.forms as any))

    const resultsP = Promise.all(
        [
            locationFocusListsP,
            issuesP,
            smartDataOrderingsP,
            actionTypesP,
            unitsP,
            unitTypesP,
            unitListsP,
            formsWriteP,
            campaignsP,
            fixesWriteP,
            reportsP,
            locationsP,
            timelineEventsP,
            reportsP,
            smartDataP,
            feedbacksP,
            I18N_sync(syncResult.response.language).then(() => {
                // pf sync is dependant on slugs having synced (img caching of slugified urls)
                return Sync_syncProcessFlow(options.since)
            }),
            Cale_sync()
                .catch(logger.error)
                // A report can only upload once it's calendar event has uploaded first
                // Otherwise it would take 2 syncs to upload a report:
                // One to upload calendar event and one to upload the report
                .then(() => Rpts_upload().catch(logger.error))

                // Posts need to sync after reports because report posts made on
                // the app do not have all the information for a report post. Namely
                // the report id and, for activity reports, the report status.
                // Report posts are uploaded with a report but downloaded in post sync
                // Set requireUnitEventPosts to false as we don't want to sync unit event posts until they are required
                .then(() => Post_sync({ requireUnitEventPosts: false })),
            Sync_syncConfirmedActions(),
            Sync_syncCommon(logger),
            Gami_sync(),
            Logg_sync(),
        ].map((p) => p && p.catch(logger.error))
    )

    // Can only run deletion code once the latest unit list & form definitions are in the DB
    await Sync_clearOldData(hive, syncResult).catch(logger.error)

    logger.log(`waiting for parallel syncs`)
    const results = await resultsP

    logger.log(`syncs complete`)

    hive.dispatch(syncAll())

    const statuses = results
        .filter((entry: any) => entry && entry.status)
        .map((entry: any) => ({ name: entry.name, status: entry.status }))

    // cache the images for units,  only for field merchandising customers
    if (!Cust_hasSingleLocation()) {
        Imag_cacheImages(Imag_getImageUrlsForUnits(syncResult.response.units))
    }

    return { status: statuses }
}

function requestSyncsOnBus(bus: Base_Bus) {
    Todo_requestSync('Regular sync', bus)
}
